import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import JSZip from "jszip";
import { 
  Megaphone, 
  Download, 
  Trash2, 
  Plus, 
  Loader2,
  Calendar,
  Filter,
  TrendingUp,
  BarChart3,
  Image,
  CheckSquare,
  Square,
  Package,
  FileImage,
  Share2
} from "lucide-react";

// SVG Icons for social platforms
const InstagramIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const FacebookIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
  </svg>
);

const TwitterIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const PinterestIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
  </svg>
);

const LinkedInIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { ShareDialog } from "@/components/ad-studio/ShareDialog";

interface AdCreative {
  id: string;
  product_image_url: string;
  creative_type: string;
  platform: string;
  format: string;
  aspect_ratio: string;
  output_url: string;
  text_config: any;
  style_config: any;
  created_at: string;
}

const PLATFORM_CONFIG: Record<string, { color: string; icon: React.FC<{ className?: string }>; label: string }> = {
  instagram: { color: "bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400", icon: InstagramIcon, label: "Instagram" },
  facebook: { color: "bg-[#1877F2]", icon: FacebookIcon, label: "Facebook" },
  tiktok: { color: "bg-black dark:bg-zinc-900", icon: TikTokIcon, label: "TikTok" },
  twitter: { color: "bg-black dark:bg-zinc-900", icon: TwitterIcon, label: "X (Twitter)" },
  pinterest: { color: "bg-[#E60023]", icon: PinterestIcon, label: "Pinterest" },
  linkedin: { color: "bg-[#0A66C2]", icon: LinkedInIcon, label: "LinkedIn" },
};

const CREATIVE_TYPES: Record<string, string> = {
  showcase: "Showcase",
  promo: "Promo/Soldes",
  testimonial: "Témoignage",
  beforeafter: "Avant/Après",
  collection: "Collection",
};

const DATE_FILTERS = [
  { value: "all", label: "Tout le temps" },
  { value: "today", label: "Aujourd'hui" },
  { value: "week", label: "Cette semaine" },
  { value: "month", label: "Ce mois" },
  { value: "quarter", label: "3 derniers mois" },
];

const EXPORT_FORMATS = [
  { value: "png", label: "PNG", description: "Haute qualité, fond transparent" },
  { value: "jpg", label: "JPG", description: "Taille réduite, idéal web" },
  { value: "webp", label: "WebP", description: "Moderne, excellent ratio qualité/taille" },
];

export default function AdHistory() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [platformFilter, setPlatformFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [dateFilter, setDateFilter] = useState<string>("all");
  const [selectedCreatives, setSelectedCreatives] = useState<Set<string>>(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportFormat, setExportFormat] = useState<string>("png");
  const [isExporting, setIsExporting] = useState(false);
  const [shareCreative, setShareCreative] = useState<AdCreative | null>(null);

  const { data: creatives, isLoading } = useQuery({
    queryKey: ["ad-creatives"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from("ad_creatives")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as AdCreative[];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("ad_creatives")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ad-creatives"] });
      toast({ title: "Créative supprimée" });
    },
    onError: () => {
      toast({
        title: "Erreur",
        description: "Impossible de supprimer la créative",
        variant: "destructive",
      });
    },
  });

  const downloadCreative = async (creative: AdCreative) => {
    try {
      const response = await fetch(creative.output_url);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${creative.platform}-${creative.format.replace(/\s+/g, "-").toLowerCase()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de télécharger l'image",
        variant: "destructive",
      });
    }
  };

  // Bulk export functionality
  const convertImageToFormat = async (imageUrl: string, format: string): Promise<Blob> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        
        if (format === 'png') {
          resolve(blob);
          return;
        }

        const img = new window.Image();
        img.crossOrigin = "anonymous";
        
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }

          // For JPG, fill white background
          if (format === 'jpg') {
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
          }
          
          ctx.drawImage(img, 0, 0);
          
          const mimeType = format === 'jpg' ? 'image/jpeg' : `image/${format}`;
          const quality = format === 'jpg' ? 0.92 : 0.95;
          
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Could not convert image'));
            }
          }, mimeType, quality);
        };
        
        img.onerror = () => reject(new Error('Could not load image'));
        img.src = URL.createObjectURL(blob);
      } catch (error) {
        reject(error);
      }
    });
  };

  const handleBulkExport = async () => {
    if (selectedCreatives.size === 0) return;
    
    setIsExporting(true);
    const zip = new JSZip();
    const selectedItems = filteredCreatives.filter(c => selectedCreatives.has(c.id));
    
    try {
      for (let i = 0; i < selectedItems.length; i++) {
        const creative = selectedItems[i];
        const blob = await convertImageToFormat(creative.output_url, exportFormat);
        const fileName = `${creative.platform}-${creative.format.replace(/\s+/g, "-").toLowerCase()}-${i + 1}.${exportFormat}`;
        zip.file(fileName, blob);
      }
      
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `floop-creatives-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast({
        title: "Export réussi",
        description: `${selectedItems.length} créatives exportées en ${exportFormat.toUpperCase()}`,
      });
      
      setShowExportDialog(false);
      setSelectedCreatives(new Set());
      setIsSelectionMode(false);
    } catch (error) {
      toast({
        title: "Erreur d'export",
        description: "Impossible d'exporter les créatives",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const toggleCreativeSelection = (id: string) => {
    const newSelection = new Set(selectedCreatives);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedCreatives(newSelection);
  };

  const selectAllCreatives = () => {
    if (selectedCreatives.size === filteredCreatives.length) {
      setSelectedCreatives(new Set());
    } else {
      setSelectedCreatives(new Set(filteredCreatives.map(c => c.id)));
    }
  };

  // Filter by date
  const getDateFilteredCreatives = (items: AdCreative[]) => {
    if (dateFilter === "all") return items;
    
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    return items.filter((c) => {
      const createdAt = new Date(c.created_at);
      switch (dateFilter) {
        case "today":
          return createdAt >= startOfDay;
        case "week":
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          return createdAt >= weekAgo;
        case "month":
          const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
          return createdAt >= monthAgo;
        case "quarter":
          const quarterAgo = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
          return createdAt >= quarterAgo;
        default:
          return true;
      }
    });
  };

  const filteredCreatives = useMemo(() => {
    if (!creatives) return [];
    
    let filtered = getDateFilteredCreatives(creatives);
    
    if (platformFilter !== "all") {
      filtered = filtered.filter((c) => c.platform === platformFilter);
    }
    
    if (typeFilter !== "all") {
      filtered = filtered.filter((c) => c.creative_type === typeFilter);
    }
    
    return filtered;
  }, [creatives, platformFilter, typeFilter, dateFilter]);

  // Statistics calculations
  const stats = useMemo(() => {
    if (!creatives || creatives.length === 0) {
      return {
        total: 0,
        thisMonth: 0,
        byPlatform: {},
        byType: {},
        recentTrend: 0,
      };
    }

    const now = new Date();
    const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
    const twoMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, now.getDate());

    const thisMonth = creatives.filter((c) => new Date(c.created_at) >= monthAgo).length;
    const lastMonth = creatives.filter((c) => {
      const date = new Date(c.created_at);
      return date >= twoMonthsAgo && date < monthAgo;
    }).length;

    const byPlatform: Record<string, number> = {};
    const byType: Record<string, number> = {};

    creatives.forEach((c) => {
      byPlatform[c.platform] = (byPlatform[c.platform] || 0) + 1;
      byType[c.creative_type] = (byType[c.creative_type] || 0) + 1;
    });

    const recentTrend = lastMonth > 0 ? Math.round(((thisMonth - lastMonth) / lastMonth) * 100) : thisMonth > 0 ? 100 : 0;

    return {
      total: creatives.length,
      thisMonth,
      byPlatform,
      byType,
      recentTrend,
    };
  }, [creatives]);

  const platforms = [...new Set(creatives?.map((c) => c.platform) || [])];
  const types = [...new Set(creatives?.map((c) => c.creative_type) || [])];

  return (
    <AppLayout>
      <div className="container max-w-7xl py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <Megaphone className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Historique des Créatives</h1>
              <p className="text-muted-foreground">
                Gérez et analysez vos publicités générées
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {filteredCreatives.length > 0 && (
              <Button
                variant={isSelectionMode ? "secondary" : "outline"}
                onClick={() => {
                  setIsSelectionMode(!isSelectionMode);
                  if (isSelectionMode) {
                    setSelectedCreatives(new Set());
                  }
                }}
              >
                {isSelectionMode ? (
                  <>
                    <CheckSquare className="w-4 h-4 mr-2" />
                    Annuler
                  </>
                ) : (
                  <>
                    <Square className="w-4 h-4 mr-2" />
                    Sélectionner
                  </>
                )}
              </Button>
            )}
            <Button onClick={() => navigate("/ad-studio")} className="bg-gradient-floop hover:opacity-90 shadow-lg shadow-primary/30">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle créative
            </Button>
          </div>
        </div>

        {/* Selection toolbar */}
        {isSelectionMode && (
          <div className="flex items-center justify-between p-4 mb-6 bg-primary/10 border border-primary/20 rounded-xl">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={selectAllCreatives}
                className="text-primary"
              >
                {selectedCreatives.size === filteredCreatives.length ? (
                  <>
                    <CheckSquare className="w-4 h-4 mr-2" />
                    Tout désélectionner
                  </>
                ) : (
                  <>
                    <Square className="w-4 h-4 mr-2" />
                    Tout sélectionner
                  </>
                )}
              </Button>
              <span className="text-sm text-muted-foreground">
                {selectedCreatives.size} sélectionné{selectedCreatives.size > 1 ? "s" : ""}
              </span>
            </div>
            <Button
              onClick={() => setShowExportDialog(true)}
              disabled={selectedCreatives.size === 0}
              className="bg-gradient-floop hover:opacity-90"
            >
              <Package className="w-4 h-4 mr-2" />
              Exporter en ZIP ({selectedCreatives.size})
            </Button>
          </div>
        )}

        {/* Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total créatives</p>
                  <p className="text-3xl font-bold text-primary">{stats.total}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <Image className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-accent/10 to-accent/5 border-accent/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Ce mois</p>
                  <p className="text-3xl font-bold text-accent">{stats.thisMonth}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-accent" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500/10 to-green-500/5 border-green-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Tendance</p>
                  <p className={cn(
                    "text-3xl font-bold",
                    stats.recentTrend >= 0 ? "text-green-500" : "text-red-500"
                  )}>
                    {stats.recentTrend >= 0 ? "+" : ""}{stats.recentTrend}%
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-violet-500/10 to-violet-500/5 border-violet-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Plateformes</p>
                  <p className="text-3xl font-bold text-violet-500">{Object.keys(stats.byPlatform).length}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-violet-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Platform Distribution */}
        {Object.keys(stats.byPlatform).length > 0 && (
          <Card className="mb-8">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                Répartition par plateforme
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {Object.entries(stats.byPlatform)
                  .sort(([, a], [, b]) => b - a)
                  .map(([platform, count]) => {
                    const config = PLATFORM_CONFIG[platform];
                    const percentage = Math.round((count / stats.total) * 100);
                    const Icon = config?.icon || Image;
                    
                    return (
                      <button
                        key={platform}
                        onClick={() => setPlatformFilter(platform === platformFilter ? "all" : platform)}
                        className={cn(
                          "flex items-center gap-2 px-4 py-2 rounded-xl border transition-all",
                          platformFilter === platform 
                            ? "border-primary bg-primary/10" 
                            : "border-border hover:border-primary/50 hover:bg-secondary"
                        )}
                      >
                        <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", config?.color || "bg-gray-500")}>
                          <Icon className="w-4 h-4 text-white" />
                        </div>
                        <div className="text-left">
                          <p className="text-sm font-medium">{config?.label || platform}</p>
                          <p className="text-xs text-muted-foreground">{count} ({percentage}%)</p>
                        </div>
                      </button>
                    );
                  })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-secondary/50 rounded-xl">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">Filtres :</span>
          </div>
          
          <Select value={platformFilter} onValueChange={setPlatformFilter}>
            <SelectTrigger className="w-40 bg-background">
              <SelectValue placeholder="Plateforme" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes plateformes</SelectItem>
              {platforms.map((platform) => (
                <SelectItem key={platform} value={platform}>
                  {PLATFORM_CONFIG[platform]?.label || platform}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-40 bg-background">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les types</SelectItem>
              {types.map((type) => (
                <SelectItem key={type} value={type}>
                  {CREATIVE_TYPES[type] || type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={dateFilter} onValueChange={setDateFilter}>
            <SelectTrigger className="w-40 bg-background">
              <SelectValue placeholder="Période" />
            </SelectTrigger>
            <SelectContent>
              {DATE_FILTERS.map((filter) => (
                <SelectItem key={filter.value} value={filter.value}>
                  {filter.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {(platformFilter !== "all" || typeFilter !== "all" || dateFilter !== "all") && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => {
                setPlatformFilter("all");
                setTypeFilter("all");
                setDateFilter("all");
              }}
            >
              Réinitialiser
            </Button>
          )}

          <div className="ml-auto text-sm text-muted-foreground">
            {filteredCreatives.length} résultat{filteredCreatives.length > 1 ? "s" : ""}
          </div>
        </div>

        {/* Creative Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredCreatives && filteredCreatives.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredCreatives.map((creative) => {
              const config = PLATFORM_CONFIG[creative.platform];
              const Icon = config?.icon || Image;
              const isSelected = selectedCreatives.has(creative.id);
              
              return (
                <Card 
                  key={creative.id} 
                  className={cn(
                    "group overflow-hidden transition-all",
                    isSelectionMode && "cursor-pointer",
                    isSelected && "ring-2 ring-primary shadow-lg shadow-primary/20"
                  )}
                  onClick={() => isSelectionMode && toggleCreativeSelection(creative.id)}
                >
                  <div className="relative">
                    <img
                      src={creative.output_url}
                      alt={`${creative.platform} ${creative.format}`}
                      className="w-full h-auto"
                      loading="lazy"
                    />
                    
                    {/* Selection checkbox overlay */}
                    {isSelectionMode && (
                      <div className="absolute top-2 right-2 z-10">
                        <div className={cn(
                          "w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all",
                          isSelected 
                            ? "bg-primary border-primary" 
                            : "bg-white/80 border-white/50 backdrop-blur-sm"
                        )}>
                          {isSelected && <CheckSquare className="w-4 h-4 text-white" />}
                        </div>
                      </div>
                    )}
                    
                    {!isSelectionMode && (
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                          <Badge variant="secondary" className="bg-white/20 backdrop-blur-sm text-white border-0">
                            {creative.format}
                          </Badge>
                          <div className="flex gap-1">
                            <Button
                              size="icon"
                              variant="secondary"
                              className="w-8 h-8 bg-white/20 backdrop-blur-sm hover:bg-white/30"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShareCreative(creative);
                              }}
                            >
                              <Share2 className="w-4 h-4 text-white" />
                            </Button>
                            <Button
                              size="icon"
                              variant="secondary"
                              className="w-8 h-8 bg-white/20 backdrop-blur-sm hover:bg-white/30"
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadCreative(creative);
                              }}
                            >
                              <Download className="w-4 h-4 text-white" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="icon" variant="destructive" className="w-8 h-8">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Supprimer cette créative ?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Cette action est irréversible.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => deleteMutation.mutate(creative.id)}
                                  >
                                    Supprimer
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Platform badge */}
                    <div className={cn(
                      "absolute top-2 left-2 w-7 h-7 rounded-lg flex items-center justify-center shadow-lg",
                      config?.color || "bg-gray-500"
                    )}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                  </div>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm">{config?.label || creative.platform}</span>
                      <Badge variant="outline" className="text-[10px]">
                        {CREATIVE_TYPES[creative.creative_type] || creative.creative_type}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Calendar className="w-3 h-3" />
                      {new Date(creative.created_at).toLocaleDateString("fr-FR", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
              <Megaphone className="w-10 h-10 text-muted-foreground" />
            </div>
            <div className="text-center">
              <p className="text-lg font-medium">Aucune créative trouvée</p>
              <p className="text-muted-foreground text-sm">
                {platformFilter !== "all" || typeFilter !== "all" || dateFilter !== "all"
                  ? "Essayez de modifier vos filtres"
                  : "Commencez par créer votre première publicité"}
              </p>
            </div>
            <Button onClick={() => navigate("/ad-studio")} className="bg-gradient-floop hover:opacity-90">
              <Plus className="w-4 h-4 mr-2" />
              Créer ma première publicité
            </Button>
          </div>
        )}
      </div>

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-primary" />
              Export en masse
            </DialogTitle>
            <DialogDescription>
              Exportez {selectedCreatives.size} créative{selectedCreatives.size > 1 ? "s" : ""} dans le format de votre choix
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <label className="text-sm font-medium">Format d'export</label>
              <div className="grid gap-2">
                {EXPORT_FORMATS.map((format) => (
                  <button
                    key={format.value}
                    onClick={() => setExportFormat(format.value)}
                    className={cn(
                      "flex items-center gap-3 p-3 rounded-xl border transition-all text-left",
                      exportFormat === format.value
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-primary/50 hover:bg-secondary"
                    )}
                  >
                    <div className={cn(
                      "w-10 h-10 rounded-lg flex items-center justify-center",
                      exportFormat === format.value ? "bg-primary text-primary-foreground" : "bg-secondary"
                    )}>
                      <FileImage className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-medium">{format.label}</p>
                      <p className="text-xs text-muted-foreground">{format.description}</p>
                    </div>
                    {exportFormat === format.value && (
                      <CheckSquare className="w-5 h-5 text-primary ml-auto" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleBulkExport} 
              disabled={isExporting}
              className="bg-gradient-floop hover:opacity-90"
            >
              {isExporting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Export en cours...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Télécharger ZIP
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <ShareDialog
        open={!!shareCreative}
        onOpenChange={(open) => !open && setShareCreative(null)}
        imageUrl={shareCreative?.output_url || ""}
        creativeName={shareCreative?.format}
      />
    </AppLayout>
  );
}
